// Pure ergonomic fingering: assign a finger number (1 = thumb … 5 = pinky) to each
// note, per hand, as a display aid (F4). A Viterbi over the melodic line minimises a
// hand-shape transition cost (interval ≈ 2 semitones/finger, with thumb-under/over
// crossings); simultaneous notes (chords) take canonical shapes spread by pitch.
// A heuristic, not an engraver's fingering — good enough to guide, cheap to compute.
import type { Hand, NoteEvent } from "../types";

/** Onsets within this window are one chord slice. */
const ONSET_EPS = 0.03;
const FINGERS = [1, 2, 3, 4, 5] as const;

// Canonical chord fingerings (right hand, ascending pitch → finger). The left hand
// mirrors (reversed), so its lowest note takes the pinky.
const CHORD_RH: Record<number, number[]> = {
  2: [1, 5],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 3, 4, 5],
};

function chordFingers(k: number, hand: Hand): number[] {
  const rh = CHORD_RH[k] ?? FINGERS.filter((_, i) => i < Math.min(5, k)).map((f) => f as number);
  return hand === "left" ? [...rh].reverse() : rh;
}

interface Slice {
  pitch: number; // the line-carrying edge: RH top / LH bottom
  notes: NoteEvent[]; // ascending pitch
}

/** Group a hand's notes into time-ordered slices (chords share an onset). */
function toSlices(notes: NoteEvent[], hand: Hand): Slice[] {
  const sorted = [...notes].sort((a, b) => a.onset - b.onset || a.midi - b.midi);
  const slices: Slice[] = [];
  let cur: NoteEvent[] = [];
  let start = Number.NEGATIVE_INFINITY;
  const flush = (): void => {
    if (!cur.length) return;
    const asc = [...cur].sort((a, b) => a.midi - b.midi);
    const edge = hand === "left" ? asc[0] : asc[asc.length - 1];
    slices.push({ pitch: edge?.midi ?? 0, notes: asc });
  };
  for (const n of sorted) {
    if (n.onset - start > ONSET_EPS) {
      flush();
      cur = [];
      start = n.onset;
    }
    cur.push(n);
  }
  flush();
  return slices;
}

/** Cost of moving the anchor from finger `pf` to `f` over `d` semitones. */
function step(pf: number, f: number, d: number, hand: Hand): number {
  const eff = d * (hand === "left" ? -1 : 1); // >0 = moving toward the pinky
  if (eff > 0 && f < pf) return 1 + Math.max(0, eff - 3) * 0.5; // thumb-under reset
  if (eff < 0 && f > pf) return 1 + Math.max(0, -eff - 3) * 0.5; // thumb-over (descending)
  // ~2 semitones per finger, but adjacent fingers comfortably span 1–3 semitones,
  // so a ±1.5 tolerance is free — an ascending five-finger run stays 1·2·3·4·5
  // instead of (equally-costed) crossing the thumb under on a half-step.
  const stretch = Math.max(0, Math.abs((f - pf) * 2 - eff) - 1.5);
  return stretch + (f === pf && d !== 0 ? 1.5 : 0); // penalise sliding one finger
}

/** Per-slice candidate anchor fingers: a single note is flexible (1–5); a chord is
 *  pinned to its canonical anchor finger (RH top / LH bottom). */
function candidates(s: Slice, hand: Hand): number[] {
  if (s.notes.length === 1) return [...FINGERS];
  const fs = chordFingers(s.notes.length, hand);
  return [hand === "left" ? (fs[0] ?? 5) : (fs[fs.length - 1] ?? 5)];
}

function fingerHand(notes: NoteEvent[], hand: Hand, out: Map<NoteEvent, number>): void {
  const slices = toSlices(notes, hand);
  if (!slices.length) return;
  const cand = slices.map((s) => candidates(s, hand));

  // Viterbi over anchor fingers.
  let prevCost = (cand[0] ?? []).map(() => 0);
  const back: number[][] = [(cand[0] ?? []).map(() => -1)];
  for (let i = 1; i < slices.length; i++) {
    const here = cand[i] ?? [];
    const there = cand[i - 1] ?? [];
    const d = (slices[i]?.pitch ?? 0) - (slices[i - 1]?.pitch ?? 0);
    const cost = here.map(() => Number.POSITIVE_INFINITY);
    const ptr = here.map(() => 0);
    here.forEach((f, hi) => {
      there.forEach((pf, pi) => {
        const c = (prevCost[pi] ?? Infinity) + step(pf, f, d, hand);
        if (c < (cost[hi] ?? Infinity)) {
          cost[hi] = c;
          ptr[hi] = pi;
        }
      });
    });
    prevCost = cost;
    back.push(ptr);
  }

  // Backtrack the best anchor-finger path.
  let bi = 0;
  prevCost.forEach((c, i) => {
    if (c < (prevCost[bi] ?? Infinity)) bi = i;
  });
  const anchorIdx: number[] = [];
  for (let i = slices.length - 1; i >= 0; i--) {
    anchorIdx[i] = bi;
    bi = back[i]?.[bi] ?? 0;
  }

  // Emit fingers: single → its anchor finger; chord → canonical shape by pitch.
  slices.forEach((s, i) => {
    if (s.notes.length === 1) {
      const f = cand[i]?.[anchorIdx[i] ?? 0];
      if (s.notes[0] && f) out.set(s.notes[0], f);
    } else {
      const fs = chordFingers(s.notes.length, hand);
      s.notes.forEach((n, j) => {
        const f = fs[j];
        if (f) out.set(n, f);
      });
    }
  });
}

/** Finger (1–5) per note, keyed by the note object. Unknown-hand notes (single-track)
 *  are fingered as a right hand. */
export function assignFingers(notes: readonly NoteEvent[]): Map<NoteEvent, number> {
  const out = new Map<NoteEvent, number>();
  fingerHand(
    notes.filter((n) => n.hand === "left"),
    "left",
    out,
  );
  fingerHand(
    notes.filter((n) => n.hand !== "left"),
    "right",
    out,
  );
  return out;
}
